import React from "react";
import ChatComposerReplyPreview from "./ChatComposerReplyPreview";
import DsButton from "../../DsButton";
import DsModal from "../../DsModal";
import PersonAvatar from "../../avatar/PersonAvatar";

function ChatComposerView({
  keyboardOffset,
  composerAnchorRef,
  replyDraft,
  onReplyCancel,
  errors,
  pendingImages,
  MAX_IMAGES,
  clearPendingImages,
  removePendingImage,
  imageInputRef,
  acceptImageAttr,
  doSendImages,
  imageInputId,
  canSendFiles,
  sendingKind,
  readOnly,
  fileInputRef,
  acceptDocAttr,
  doSendFile,
  fileInputId,
  showMentionMenu,
  mentionHelperText,
  hasMultipleMentionTypes,
  mentionActiveTab,
  setMentionActiveTab,
  mentionPersonCount,
  mentionPartCount,
  visibleMentionCandidates,
  mentionActiveIndex,
  insertMentionCandidate,
  isCompact,
  composerShellRef,
  composerMentionRanges,
  mentionHighlightRef,
  composerHighlightNodes,
  textareaRef,
  placeholderText,
  text,
  handleTextChange,
  handlePaste,
  handleKeyDown,
  syncMentionHighlightScroll,
  updateMentionTrigger,
  notifyBlocked,
  MAX_TEXTAREA_PX,
  textTooLong,
  handleSend,
  lateNightPrompt,
  closeLateNightPrompt,
  uidRef,
  CHAT_NOTIFICATION_IMMEDIATE,
  CHAT_NOTIFICATION_DELAY_UNTIL_MORNING,
  canCreateQuestion,
  openQuestionModal,
}) {
  const renderAttachButtons = (opts = {}) => {
    const { includeImage = true } = opts;
    const disabled = !canSendFiles || !!sendingKind || readOnly;
    const questionDisabled = !!sendingKind || readOnly;
    const cls = ["composer-icon-btn", disabled ? "is-disabled" : ""].join(" ");
    return (
      <>
        {canCreateQuestion ? (
          <button
            type="button"
            className={["composer-icon-btn", questionDisabled ? "is-disabled" : ""].join(" ")}
            onClick={openQuestionModal}
            disabled={questionDisabled}
            title="質問メッセージを作成"
            aria-label="質問メッセージを作成"
          >
            <i className="fa-solid fa-circle-question" />
          </button>
        ) : null}
        {includeImage && (
          <button
            type="button"
            className={cls}
            onClick={(event) => {
              if (disabled) return;
              if (readOnly) {
                event.preventDefault();
                notifyBlocked();
                return;
              }
              if (imageInputRef.current) imageInputRef.current.click();
            }}
            title="画像を選択（最大5枚）"
            aria-label="画像を選択（最大5枚）"
          >
            <i className="fa-solid fa-image" />
          </button>
        )}

        <button
          type="button"
          className={cls}
          onClick={(event) => {
            if (disabled) return;
            if (readOnly) {
              event.preventDefault();
              notifyBlocked();
              return;
            }
            if (fileInputRef.current) fileInputRef.current.click();
          }}
          title="ファイルを選択"
          aria-label="ファイルを選択"
        >
          <i className="fa-solid fa-file" />
        </button>
      </>
    );
  };

  return (
    <div
      ref={composerAnchorRef}
      className="chat-composer"
      style={keyboardOffset ? { transform: `translateY(-${keyboardOffset}px)` } : undefined}
    >
      <ChatComposerReplyPreview reply={replyDraft} onCancel={onReplyCancel} />
      {errors.length > 0 && (
        <div className="composer-alert-wrap">
          <div className="composer-alert">
            <i className="fa-solid fa-circle-exclamation alert-icon" aria-hidden="true" />
            <div>
              {errors.map((message, index) => (
                <div key={index}>• {message}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pendingImages.length > 0 && (
        <div className="chat-composer__pendingImages" aria-label="送信待機中の画像">
          <div className="chat-composer__pendingImagesHeader">
            <span className="chat-composer__pendingImagesLabel">
              送信待機中の画像 {pendingImages.length}/{MAX_IMAGES}
            </span>
            {pendingImages.length > 1 ? (
              <button
                type="button"
                className="chat-composer__pendingImagesClear"
                onClick={clearPendingImages}
              >
                すべて削除
              </button>
            ) : null}
          </div>
          <div className="chat-composer__pendingImagesList">
            {pendingImages.map((entry) => (
              <div key={entry.id} className="chat-composer__pendingImageCard">
                <div className="chat-composer__pendingImageThumb">
                  {entry.previewUrl ? (
                    <img src={entry.previewUrl} alt={entry.displayName} />
                  ) : (
                    <div className="chat-composer__pendingImageFallback">
                      <i className="fa-regular fa-image" aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="chat-composer__pendingImageMeta">
                  <span className="chat-composer__pendingImageName">{entry.displayName}</span>
                </div>
                <button
                  type="button"
                  className="chat-composer__pendingImageRemove"
                  onClick={() => removePendingImage(entry.id)}
                  aria-label={`${entry.displayName} を削除`}
                  title="待機中画像を削除"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 非表示の実ファイル入力（ダイアログ用） */}
      <input
        ref={imageInputRef}
        type="file"
        accept={acceptImageAttr}
        multiple
        onChange={(event) => doSendImages(event.target.files)}
        id={imageInputId}
        disabled={!canSendFiles || !!sendingKind || readOnly}
        className="hidden-input"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptDocAttr}
        onChange={(event) => doSendFile(event.target.files?.[0] || null)}
        id={fileInputId}
        disabled={!canSendFiles || !!sendingKind || readOnly}
        className="hidden-input"
      />

      <div className={`chat-composer__surface${showMentionMenu ? " has-mention-menu" : ""}`}>
        {showMentionMenu ? (
          <div className="chat-composer__mentionMenu" role="listbox" aria-label="メンション候補">
            {mentionHelperText ? (
              <div className="chat-composer__mentionHelp">
                {mentionHelperText}
              </div>
            ) : null}
            {hasMultipleMentionTypes ? (
              <div className="chat-composer__mentionTabs" role="presentation">
                <button
                  type="button"
                  className={`chat-composer__mentionTab${mentionActiveTab === "person" ? " is-active" : ""}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    setMentionActiveTab("person");
                  }}
                >
                  人物 <span>{mentionPersonCount}</span>
                </button>
                <button
                  type="button"
                  className={`chat-composer__mentionTab${mentionActiveTab === "part" ? " is-active" : ""}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    setMentionActiveTab("part");
                  }}
                >
                  パート <span>{mentionPartCount}</span>
                </button>
              </div>
            ) : null}
            <div className="chat-composer__mentionOptions">
              {visibleMentionCandidates.length > 0 ? (
                visibleMentionCandidates.map((candidate, index) => {
                  const isActive = index === mentionActiveIndex;
                  const isPart = candidate.type === "part";
                  return (
                    <button
                      type="button"
                      key={`${candidate.type}:${candidate.role}:${candidate.id}`}
                      className={`chat-composer__mentionOption${isActive ? " is-active" : ""}`}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        insertMentionCandidate(candidate);
                      }}
                      role="option"
                      aria-selected={isActive ? "true" : "false"}
                    >
                      {isPart ? (
                        <span className="chat-composer__mentionAvatar is-part">
                          <i className="fa-solid fa-users" aria-hidden="true" />
                        </span>
                      ) : (
                        <PersonAvatar
                          role={candidate.role}
                          name={candidate.label}
                          profilePicture={candidate.profilePicture}
                          imageUrl={candidate.avatarUrl}
                          size={24}
                          className="chat-composer__mentionAvatar"
                          ariaHidden
                        />
                      )}
                      <span className="chat-composer__mentionBody">
                        <span className="chat-composer__mentionNameLine">
                          <span className="chat-composer__mentionName">@{candidate.label}</span>
                          {candidate.subtitle ? (
                            <span className="chat-composer__mentionMeta">{candidate.subtitle}</span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="chat-composer__mentionEmpty">
                  この条件の候補はありません。
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className={`composer-shell ${isCompact ? "is-compact" : ""}`} ref={composerShellRef}>
          <div className="composer-left">
            {renderAttachButtons()}
          </div>

          <div className="composer-input-layer">
            {composerMentionRanges.length > 0 ? (
              <div
                ref={mentionHighlightRef}
                className="composer-input-highlight"
                data-testid="composer-input-highlight"
                aria-hidden="true"
              >
                {composerHighlightNodes}
              </div>
            ) : null}
            <textarea
              ref={textareaRef}
              className={`composer-textarea${composerMentionRanges.length > 0 ? " has-mention-highlights" : ""}`}
              placeholder={placeholderText}
              value={text}
              onChange={handleTextChange}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onScroll={syncMentionHighlightScroll}
              onClick={(event) => updateMentionTrigger(event.currentTarget.value, event.currentTarget.selectionStart)}
              onKeyUp={(event) => {
                if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)) return;
                updateMentionTrigger(event.currentTarget.value, event.currentTarget.selectionStart);
              }}
              onFocus={() => {
                if (readOnly) notifyBlocked();
              }}
              disabled={!!sendingKind}
              style={{ maxHeight: MAX_TEXTAREA_PX, overflowY: "hidden" }}
            />
          </div>

          <div className="composer-right">
            <button
              className={[
                "composer-icon-btn",
                "composer-send-btn",
                textTooLong || (!text.trim() && pendingImages.length === 0) || !!sendingKind || readOnly
                  ? "is-disabled"
                  : "",
              ].join(" ")}
              onClick={handleSend}
              disabled={!!sendingKind || textTooLong || (!text.trim() && pendingImages.length === 0) || readOnly}
              title="送信（Windows/Linux: Alt+Enter、Mac: ⌥+Enter または ⌘+Enter）"
              aria-label="送信"
            >
              <i className="fas fa-paper-plane" />
            </button>
          </div>
        </div>
      </div>

      <DsModal
        open={Boolean(lateNightPrompt)}
        onClose={() => closeLateNightPrompt(null)}
        title="深夜時間帯の通知"
        size="sm"
        className="chat-composer__lateNightDialog"
        bodyClassName="chat-composer__lateNightContent"
        footer={(
          <>
            <DsButton
              variant="secondary"
              size="sm"
              block
              onClick={() => closeLateNightPrompt(CHAT_NOTIFICATION_IMMEDIATE)}
            >
              今すぐ通知
            </DsButton>
            <DsButton
              size="sm"
              block
              onClick={() => closeLateNightPrompt(CHAT_NOTIFICATION_DELAY_UNTIL_MORNING)}
            >
              朝に通知
            </DsButton>
          </>
        )}
      >
        <div className="chat-composer__lateNightIcon" aria-hidden="true">
          <i className="fa-regular fa-clock" />
        </div>
        <div className="chat-composer__lateNightBody">
          <p>
            メッセージは今すぐ送信されます。受信者への通知だけ朝6時以降に回すことができます。
          </p>
        </div>
      </DsModal>

      <div className="shortcut-hint">
        Enterで改行 / 送信は <strong>Alt+Enter</strong>（Windows/Linux）・<strong>⌥Enter</strong> または <strong>⌘Enter</strong>（Mac）
      </div>
    </div>
  );
}

export default ChatComposerView;
